import { supabase } from './supabase';

/**
 * Remove a file from Supabase storage if the URL points to a Supabase bucket object.
 */
export async function tryRemoveStorageFile(urlOrPath?: string | null): Promise<void> {
  if (!urlOrPath) return;
  try {
    const match = urlOrPath.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/);
    if (match && match[1] && match[2]) {
      const bucket = match[1];
      const pathPart = match[2].split('?')[0];
      if (pathPart) {
        const path = decodeURIComponent(pathPart);
        await supabase.storage.from(bucket).remove([path]);
      }
    }
  } catch {
    // Ignore storage deletion errors
  }
}

/**
 * Permanently cascade delete a project and all associated data:
 * - Storage files (drawings, elevation renders, receipts, bill files)
 * - Client payments
 * - Material purchases & purchase bills
 * - Supplier payments
 * - Subcontractor labour & visits
 * - Subcontractor payments & contracts
 * - Site expenses
 * - Material usage & consumption batches
 * - Stock transfers (both incoming and outgoing)
 * - Project drawings & elevation records
 * - Staff access records
 * - The project record itself
 */
export async function deleteProjectCascade(projectId: string): Promise<void> {
  if (!projectId) return;

  // 1. Collect and remove files from Supabase Storage
  try {
    const { data: drawings } = await supabase
      .from('project_drawings')
      .select('file_url')
      .eq('project_id', projectId);

    if (drawings && drawings.length > 0) {
      for (const d of drawings) {
        if (d.file_url) await tryRemoveStorageFile(d.file_url);
      }
    }

    try {
      const { data: elevations } = await (supabase.from as any)('project_elevations')
        .select('file_url')
        .eq('project_id', projectId);

      if (elevations && elevations.length > 0) {
        for (const el of elevations) {
          if (el.file_url) await tryRemoveStorageFile(el.file_url);
        }
      }
    } catch {
      // Table may not exist or not have files
    }

    const { data: expenses } = await supabase
      .from('expenses')
      .select('receipt_url')
      .eq('project_id', projectId);

    if (expenses && expenses.length > 0) {
      for (const e of expenses) {
        if ((e as any).receipt_url) await tryRemoveStorageFile((e as any).receipt_url);
      }
    }
  } catch {
    // Storage deletion non-critical, continue with database cascade
  }

  // 2. Cascade delete database tables linked to this project
  // Clean up drawings & elevation records
  await supabase.from('project_drawings').delete().eq('project_id', projectId);
  try {
    await (supabase.from as any)('project_elevations').delete().eq('project_id', projectId);
  } catch {
    // Ignore if not present
  }

  // Client payments
  await supabase.from('client_payments').delete().eq('project_id', projectId);

  // Material usage lines & batches
  await supabase.from('material_usage').delete().eq('project_id', projectId);
  try {
    await (supabase.from as any)('material_usage_batches').delete().eq('project_id', projectId);
  } catch {
    // Ignore if not present
  }

  // Subcontractor labour lines, batches, payments, contracts
  await supabase.from('subcontractor_labour').delete().eq('project_id', projectId);
  try {
    await (supabase.from as any)('subcontractor_labour_batches').delete().eq('project_id', projectId);
  } catch {
    // Ignore if not present
  }
  await supabase.from('subcontractor_payments').delete().eq('project_id', projectId);
  await supabase.from('subcontractor_contracts').delete().eq('project_id', projectId);

  // Stock transfers (where from or to this project)
  await supabase
    .from('stock_transfers')
    .delete()
    .or(`from_project_id.eq.${projectId},to_project_id.eq.${projectId}`);
  try {
    await (supabase.from as any)('stock_transfer_batches')
      .delete()
      .or(`from_project_id.eq.${projectId},to_project_id.eq.${projectId}`);
  } catch {
    // Ignore if not present
  }

  // Purchases & Supplier payments
  await supabase.from('material_purchases').delete().eq('project_id', projectId);
  await supabase.from('purchase_bills').delete().eq('project_id', projectId);
  await supabase.from('supplier_payments').delete().eq('project_id', projectId);

  // Expenses
  await supabase.from('expenses').delete().eq('project_id', projectId);

  // Staff project access
  try {
    await (supabase.from as any)('staff_project_access').delete().eq('project_id', projectId);
  } catch {
    // Ignore if not present
  }

  // 3. Delete the project row
  const { error: delErr } = await supabase.from('projects').delete().eq('id', projectId);
  if (delErr) {
    // Fallback: soft-delete if foreign key or RLS prevents hard-delete
    const { error: softErr } = await supabase
      .from('projects')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', projectId);

    if (softErr) {
      throw new Error(delErr.message || softErr.message);
    }
  }
}

/**
 * Permanently cascade delete a client:
 * 1. Finds and cascades deletes all projects belonging to the client
 * 2. Deletes the client row itself
 */
export async function deleteClientCascade(clientId: string): Promise<void> {
  if (!clientId) return;

  // 1. Fetch all projects belonging to this client
  const { data: projects } = await supabase
    .from('projects')
    .select('id')
    .eq('client_id', clientId);

  if (projects && projects.length > 0) {
    for (const p of projects) {
      await deleteProjectCascade(p.id);
    }
  }

  // 2. Delete the client row
  const { error: delErr } = await supabase.from('clients').delete().eq('id', clientId);
  if (delErr) {
    const { error: softErr } = await supabase
      .from('clients')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', clientId);

    if (softErr) {
      throw new Error(delErr.message || softErr.message);
    }
  }
}
